import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { Box, Button, Flex, HStack, Text } from '@bonusly/design-system';

import { useGithubDisconnect, useGithubStatus } from '../../queries/github';
import { BizySettingsSecondaryView } from '../bizy-settings/bizy-settings-secondary-view';
import { GithubLogo } from './github-logo';
import { GithubScopePicker } from './github-scope-picker';

interface GithubManagementViewProps {
	onBack: () => void;
}

export const GithubManagementView: FC<GithubManagementViewProps> = ({ onBack }) => {
	const { t } = useTranslation('bizy_settings', { keyPrefix: 'github' });
	const { data: status, isLoading: isLoadingStatus } = useGithubStatus();
	const { mutateAsync: disconnect, isLoading: isDisconnecting } = useGithubDisconnect();
	const { track } = useAnalytics();

	const selectedOrgs = status?.selectedOrgs ?? [];
	const selectedRepos = status?.selectedRepos ?? [];
	const isConnected = status?.status === 'connected';

	const handleDisconnect = useCallback(async () => {
		try {
			await disconnect();
			track(AnalyticsEvent.Github.Disconnected);
			onBack();
		} catch {
			// Error handled by React Query
		}
	}, [disconnect, onBack, track]);

	if (isLoadingStatus) return null;

	return (
		<BizySettingsSecondaryView>
			<Box>
				<Flex align={{ base: 'stretch', md: 'flex-start' }} direction={{ base: 'column', md: 'row' }} gap={5} justify="space-between">
					<HStack align="flex-start" spacing={4}>
						<Box color="text.default" flexShrink={0} display="inline-flex" alignItems="center" mt={1}>
							<GithubLogo width="32" height="32" />
						</Box>
						<Box>
							<Text size="lg" variant="strong" color="text.default" mb={1}>
								{t('management.title')}
							</Text>
							<Text color="text.softer" size="sm" maxW="560px">
								{t('management.description')}
							</Text>
						</Box>
					</HStack>
					<Button
						colorScheme="danger"
						isLoading={isDisconnecting}
						onClick={handleDisconnect}
						size="sm"
						variant="ghost"
						alignSelf={{ base: 'flex-start', md: 'center' }}
					>
						{t('management.disconnect')}
					</Button>
				</Flex>
			</Box>

			{isConnected && <GithubScopePicker selectedOrgs={selectedOrgs} selectedRepos={selectedRepos} />}
		</BizySettingsSecondaryView>
	);
};
